import { createContext } from 'react';

export interface SubnetOption {
  /** CIDR like `10.2.0.0/16`. */
  cidr: string;
  /** How many known devices fall under this prefix. */
  count: number;
}

/**
 * Turn a fleet's device IPs into the selectable subnet groupings the pilot
 * editor offers: the 1st- (/8), 2nd- (/16), and 3rd-octet (/24) prefixes,
 * each with a device count. IPv4 only; missing / malformed IPs are skipped.
 * Sorted broadest-first (/8 → /16 → /24), then lexically.
 */
export function deriveSubnetOptions(ips: readonly (string | null)[]): SubnetOption[] {
  const counts = new Map<string, number>();
  for (const ip of ips) {
    if (ip === null) continue;
    const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.\d{1,3}$/.exec(ip);
    if (!m) continue;
    const [, a, b, c] = m;
    if (a === undefined || b === undefined || c === undefined) continue;
    for (const cidr of [`${a}.0.0.0/8`, `${a}.${b}.0.0/16`, `${a}.${b}.${c}.0/24`]) {
      counts.set(cidr, (counts.get(cidr) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .map(([cidr, count]) => ({ cidr, count }))
    .sort((x, y) => prefixLen(x.cidr) - prefixLen(y.cidr) || x.cidr.localeCompare(y.cidr));
}

function prefixLen(cidr: string): number {
  return Number(cidr.slice(cidr.indexOf('/') + 1));
}

/**
 * Subnet options derived from the tenant's live devices, provided by
 * BuilderEditor and consumed by the per-tile pilot editor's subnet picker.
 * Defaults to empty (no devices seen / fetch not done).
 */
export const PilotSubnetsContext = createContext<readonly SubnetOption[]>([]);
